import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AccordionContext } from './context';
import { APPEARANCES, TYPES } from './constants';
import { getActiveItems } from './helpers';

import { AccordionContent, AccordionGroup, AccordionHeader } from './components';

const Accordion = ({
  type = TYPES.all,
  defaultActiveItem = null,
  onToggleItem = Function.prototype,
  appearance = APPEARANCES.default,
  children,
}) => {
  const [activeItems, setActiveItems] = useState([]);

  const isOnlyOneActiveItem = type === TYPES.one;

  const toggleItem = useCallback(
    (itemId) => {
      const isActiveItem = activeItems.includes(itemId);

      if (isOnlyOneActiveItem) {
        const newActiveItem = isActiveItem ? [] : [itemId];
        setActiveItems(newActiveItem);
        onToggleItem(newActiveItem);
        return;
      }

      const newActiveItems = isActiveItem
        ? activeItems.filter((activeItem) => activeItem !== itemId)
        : [...activeItems, itemId];
      setActiveItems(newActiveItems);
      onToggleItem(newActiveItems);
    },
    [activeItems, setActiveItems, isOnlyOneActiveItem, onToggleItem]
  );

  useEffect(() => {
    setActiveItems(getActiveItems(type, defaultActiveItem));
  }, [type, defaultActiveItem]);

  return (
    <AccordionContext.Provider value={{ appearance, activeItems, toggleItem }}>
      {children}
    </AccordionContext.Provider>
  );
};

Accordion.propTypes = {
  appearance: PropTypes.oneOf(Object.values(APPEARANCES)),
  type: PropTypes.oneOf(Object.values(TYPES)),
  defaultActiveItem: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onToggleItem: PropTypes.func,
  children: PropTypes.node.isRequired,
};

export { Accordion, AccordionGroup, AccordionContent, AccordionHeader };
